import { useEffect, useRef } from "react";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Button } from "@/components/ui/button";
import { UserPlus } from "lucide-react";
import ChatMessageBubble from "./ChatMessageBubble";
import { ChatMessage } from "./types";
import { UserProfile } from "@/types/user";

type ChatMessagesContainerProps = {
  messages: ChatMessage[];
  currentUserId: string;
  recipientName?: string;
  groupMembers?: UserProfile[];
  onInviteMembers?: () => void;
};

export default function ChatMessagesContainer({ 
  messages, 
  currentUserId,
  recipientName,
  groupMembers,
  onInviteMembers
}: ChatMessagesContainerProps) {
  const messagesEndRef = useRef<HTMLDivElement | null>(null);
  
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };
  
  useEffect(() => {
    scrollToBottom();
  }, [messages]);
  
  const isGroupChat = !!groupMembers;
  const isWelcomeChat = recipientName === "Welcome Bot";
  
  // Get sender info from group members if this is a group chat
  const getSenderInfoFromGroupMembers = (senderId: string) => {
    if (!groupMembers) return null;
    return groupMembers.find(member => member.id === senderId);
  };
  
  return (
    <ScrollArea className="flex-1 p-4">
      <div className="space-y-4 pb-4">
        {messages.length === 0 ? (
          <div className="text-center text-muted-foreground py-10">
            {isWelcomeChat ? (
              <div className="space-y-4">
                <p className="text-lg font-medium">Welcome to Feel Step Forward!</p>
                <p className="text-sm">
                  You're all set up and ready to go. Here's what you can do:
                </p>
                <div className="text-left max-w-md mx-auto space-y-2 text-sm">
                  <div className="flex items-center gap-2">
                    <span className="w-2 h-2 bg-primary rounded-full"></span>
                    <span>Invite team members to start collaborating</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="w-2 h-2 bg-primary rounded-full"></span>
                    <span>Use the dashboard to track your progress</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="w-2 h-2 bg-primary rounded-full"></span>
                    <span>Set up daily check-ins to stay connected</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="w-2 h-2 bg-primary rounded-full"></span>
                    <span>Create goals and track your achievements</span>
                  </div>
                </div>
                <div className="pt-4">
                  <Button 
                    onClick={onInviteMembers}
                    className="flex items-center gap-2"
                  >
                    <UserPlus className="h-4 w-4" />
                    Invite Team Members
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground mt-4">
                  Once you invite team members, you'll be able to chat with them here!
                </p>
              </div>
            ) : (
              <p>Start a conversation with {recipientName}</p>
            )}
          </div>
        ) : (
          messages.map((message) => {
            const isCurrentUser = message.senderId === currentUserId;
            
            // For group chats, we need to get the sender info from the group members
            let senderName = message.senderName;
            let senderAvatar = message.senderAvatar;
            
            if (isGroupChat && !isCurrentUser) {
              const senderInfo = getSenderInfoFromGroupMembers(message.senderId);
              if (senderInfo) {
                senderName = senderInfo.name;
                senderAvatar = senderInfo.avatarUrl;
              }
            }
            
            return (
              <ChatMessageBubble
                key={message.id}
                message={{
                  ...message,
                  senderName,
                  senderAvatar
                }}
                isCurrentUser={isCurrentUser}
                recipientName={recipientName}
                showSenderName={isGroupChat && !isCurrentUser}
              />
            );
          })
        )}
        <div ref={messagesEndRef} />
      </div>
    </ScrollArea>
  );
}
